import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAnnexe } from "../kb/annex/useAnnexe";
import { buildCopiloteKB } from "../kb/annex/buildCopiloteKB";
import { SOLUTION_VERS_COPILOTE } from "../kb/copiloteKB";

// Correspondance nom solution → clé copiloteKB
const NOM_VERS_CLE = {
  // Soutènement
  "Stabilité des Pentes":       "stabilite_pentes",
  "Murs Cantilever":            "murs_cantilever",
  "Murs Poids":                 "murs_poids",
  "Gabions":                    "gabions",
  "Conception Écrans":          "ecrans_soutenement",
  "Vérification Écrans":        "verification_ecrans",
  "Pressions des Terres":       "pressions_terres",
  "Pentes Clouées":             "pentes_clouees",
  // Fondations superficielles
  "Semelles":                   "semelles",
  "Semelles CPT":               "semelles_cpt",
  "Tassements":                 "tassements",
  // Fondations profondes
  "Pieux":                      "pieux",
  "Pieux CPT":                  "pieux_cpt",
  "Groupe de Pieux":            "groupe_pieux",
  "Micropieux":                 "micropieux",
  "Pieux Stabilisateurs":       "stabilite_pentes",  // module le plus proche disponible
  // Géologie
  "Stratigraphie":              "stratigraphie",
  "Stabilité des Roches":       "stabilite_roches",
  // Noms alternatifs retournés par PageSolution
  "Fondation sur Pieux":        "pieux",
  "Semelles Superficielles":    "semelles",
  "Micropieux (reprise sous-œuvre)": "micropieux",
  "Écrans de Soutènement":      "ecrans_soutenement",
};

// Mapping module_key catalogue (solutions_catalogue.py) → clé COPILOTE_KB
// Utilisé pour les solutions amélioration dont modules_geo5 = [{module_key, label_fr}]
const MODULE_KEY_VERS_CLE = {
  settlement:        "tassements",
  spread_footing:    "semelles",
  spread_footing_cpt:"semelles_cpt",
  pile:              "pieux",
  pile_cpt:          "pieux_cpt",
  pile_group:        "groupe_pieux",
  micropile:         "micropieux",
  earth_pressure:    "pressions_terres",
  slope_stability:   "stabilite_pentes",
  retaining_wall:    "murs_cantilever",
  sheet_pile:        "ecrans_soutenement",
  rock_stability:    "stabilite_roches",
  seepage:           null,
  fem:               null,
};

// Formate les clés d'injection gamma_X → symbole lisible (γG, γR,h, etc.)
const _fmtKey = (k) => {
  const mR = k.match(/^gamma_r([a-z]+)$/i);
  if (mR) return `γR,${mR[1].toLowerCase()}`;
  if (k === "gamma_r") return "γR";
  const m1 = k.match(/^gamma_([a-z]+)$/i);
  if (m1) return `γ${m1[1].toUpperCase()}`;
  return k;
};

// Adaptation terminologie RPA → EC7/EC8 pour la France
function adaptEU(texte) {
  if (!texte) return texte;
  return texte
    // ── Références réglementaires ───────────────────────────────────
    .replace(/RPA 2024/g, "EC8 (EN 1998)")
    .replace(/\bRPA\b/g, "EC8")
    .replace(/DTR BC 2\.33\.?3?/g, "EN 1997-1")
    .replace(/DTR BA 2\.41/g, "EC2 (EN 1992)")
    .replace(/\bDTR\b/g, "Eurocode")
    .replace(/EN 1997-1 DA2/g, "EC7 Design Approach 2")
    .replace(/Option 1.*Standard.*coefficients de sécurité/gi, "EC7 Design Approach 1")
    .replace(/coefficients de sécurité.*Standard/gi, "EC7 partial factors")
    .replace(/S1–S4|S1\/S2\/S3\/S4/g, "soil classes A–E (EC8 §3.1)")
    // ── Zones sismiques RPA → EC8 ──────────────────────────────────
    .replace(/zones?\s*(?:≥\s*)?IIb,?\s*III,?\s*IV/gi, "zones ≥ 3 (EC8)")
    .replace(/zones?\s*III→VI/gi, "zones 3→5 (EC8)")
    .replace(/zones?\s*≥\s*IIb/gi, "zones ≥ 3 (EC8)")
    .replace(/zones?\s*≥\s*IIa/gi, "zones ≥ 2 (EC8)")
    .replace(/Zone IIb/g, "Zone 3 (EC8)")
    .replace(/Zone IIa/g, "Zone 2 (EC8)")
    .replace(/Zone III\b/g, "Zone 4 (EC8)")
    .replace(/Zone IV\b/g, "Zone 5 (EC8)")
    .replace(/Zone V\b/g, "Zone 5 (EC8)")
    .replace(/Zone VI\b/g, "Zone 5 (EC8)")
    .replace(/Zone I\b/g, "Zone 1 (EC8)")
    .replace(/zone\s*≥\s*IIa/gi, "zone ≥ 2 (EC8)")
    .replace(/zone\s*≥\s*IIb/gi, "zone ≥ 3 (EC8)")
    .replace(/zones?\s*sismiques?/gi, "seismic zones")
    .replace(/zone\s*sismique/gi, "seismic zone")
    .replace(/kh\s*=\s*A\s*×\s*η\s*\/\s*2/g, "kh = agR·S/2g (EN 1998-5)")
    .replace(/Kv\s*=\s*Kh\s*\/\s*2/g, "kv = kh/2 (EN 1998-5 §7.3.2.2)")
    .replace(/pseudo-statique/g, "pseudo-static")
    .replace(/analyse sismique/gi, "seismic analysis")
    // ── Pays / régions ─────────────────────────────────────────────
    .replace(/en Algérie/gi, "in Europe")
    .replace(/l'Algérie/g, "Europe")
    .replace(/\bAlgérie\b/g, "Europe")
    .replace(/algérien[ne]*/gi, "European")
    .replace(/Tell algérien|piémont atlasique|plaines Tell/gi, "European lowlands")
    .replace(/Mitidja|Tell|Kabylie|Atlas|Aurès|Kabylies/g, "European region")
    .replace(/Alger[,-]?Blida|Blida|Alger\b/g, "city")
    .replace(/Oran|Annaba|Béjaïa|Constantine|Thénia|Boumerdès/g, "regional city")
    .replace(/RN\d+/g, "national road")
    // ── Coefficients de sécurité ───────────────────────────────────
    .replace(/SF_min sismique/g, "SF_min seismic")
    .replace(/SF_min/g, "SF_min (EC7)")
    .replace(/SFo_sismique/g, "SFo_seismic")
    .replace(/SFs_sismique/g, "SFs_seismic")
    .replace(/SFb_sismique/g, "SFb_seismic")
    .replace(/FS portance sismique/g, "SF bearing (seismic)")
    .replace(/abaque RPA 2024/g, "pressuremeter correlation EN 1997")
    // ── Termes géotechniques ───────────────────────────────────────
    .replace(/nappe phréatique/gi, "groundwater table")
    .replace(/nappe haute/gi, "high groundwater level")
    .replace(/nappe basse/gi, "low groundwater level")
    .replace(/nappe saisonnière/gi, "seasonal groundwater")
    .replace(/\bnappe\b/gi, "groundwater")
    .replace(/stabilité globale/gi, "global stability")
    .replace(/stabilité des pentes/gi, "slope stability")
    .replace(/facteurs? de sécurité/gi, "safety factor")
    .replace(/coefficients? de sécurité/gi, "safety factor")
    .replace(/portance/gi, "bearing capacity")
    .replace(/renversement/gi, "overturning")
    .replace(/glissement/gi, "sliding")
    .replace(/tassements? différentiels?/gi, "differential settlement")
    .replace(/tassements?/gi, "settlement")
    .replace(/ferraillage/gi, "reinforcement")
    .replace(/béton armé/gi, "reinforced concrete")
    .replace(/béton cyclopéen/gi, "mass concrete")
    .replace(/béton banché/gi, "cast concrete")
    .replace(/\bmaçonnerie\b/gi, "masonry")
    .replace(/mur[s]? cantilever/gi, "cantilever wall")
    .replace(/mur[s]? poids/gi, "gravity wall")
    .replace(/mur[s]? de soutènement/gi, "retaining wall")
    .replace(/soutènement/gi, "earth retaining")
    .replace(/pentes clouées/gi, "soil nailing")
    .replace(/massif[s]? renforcé[s]?/gi, "reinforced earth")
    .replace(/fondations? profondes?/gi, "deep foundation")
    .replace(/fondations? superficielle[s]?/gi, "shallow foundation")
    .replace(/\bsemelle[s]?\b/gi, "footing")
    .replace(/couche[s]? de sol/gi, "soil layer")
    .replace(/sol de fondation/gi, "foundation soil")
    .replace(/\bremblai\b/gi, "fill")
    .replace(/\bdéblai\b/gi, "excavation")
    .replace(/\bpieux\b/gi, "piles")
    .replace(/\bpieu\b/gi, "pile")
    .replace(/micropieux/gi, "micropiles")
    .replace(/micropieu/gi, "micropile")
    .replace(/palplanches?/gi, "sheet piles")
    .replace(/paroi berlinoise/gi, "Berlin wall (soldier pile)")
    .replace(/paroi moulée/gi, "diaphragm wall")
    .replace(/fiche d['']encastrement|fiche minimale/gi, "embedment depth")
    .replace(/poussée active/gi, "active earth pressure")
    .replace(/butée passive/gi, "passive earth pressure")
    .replace(/pression[s]? des terres/gi, "earth pressures")
    .replace(/angle de frottement/gi, "friction angle")
    .replace(/poids volumique/gi, "unit weight")
    .replace(/\bargile\b/gi, "clay")
    .replace(/\blimon\b/gi, "silt")
    .replace(/\bsable\b/gi, "sand")
    .replace(/\bgravier\b/gi, "gravel")
    .replace(/\bmarne\b/gi, "marl")
    .replace(/\brocher?\b/gi, "rock")
    .replace(/surface de rupture/gi, "failure surface")
    .replace(/phase de construction/gi, "construction phase")
    .replace(/situation permanente/gi, "permanent situation")
    .replace(/situation sismique/gi, "seismic situation")
    .replace(/situation accidentelle/gi, "accidental situation")
    .replace(/situation transitoire/gi, "transient situation")
    .replace(/\bOBLIGATOIRE\b/g, "MANDATORY")
    .replace(/\bCRITIQUE\b/g, "CRITICAL")
    .replace(/\bPIÈGE\b/g, "PITFALL")
    .replace(/\bATTENTION\b/g, "WARNING")
    // ── Noms de modules KB ─────────────────────────────────────────
    .replace(/Stabilité des Pentes/g, "Slope Stability")
    .replace(/Murs Cantilever/g, "Cantilever Walls")
    .replace(/Murs Poids/g, "Gravity Walls")
    .replace(/Murs Maçonnés/g, "Masonry Walls")
    .replace(/Murs en Massif Renforcés/g, "MSE Walls")
    .replace(/Murs Redi-Rock/g, "Precast Block Walls")
    .replace(/Écrans de Soutènement/g, "Retaining Screens")
    .replace(/Vérification Écrans/g, "Sheeting Check")
    .replace(/Pressions des Terres/g, "Earth Pressures")
    .replace(/Groupe de Pieux/g, "Pile Groups")
    .replace(/Micropieux/g, "Micropiles")
    .replace(/Semelles CPT/g, "CPT Footings")
    .replace(/Pieux CPT/g, "CPT Piles")
    .replace(/Stratigraphie/g, "Stratigraphy")
    .replace(/Stabilité des Roches/g, "Rock Stability")
    .replace(/Culées/g, "Bridge Abutments")
    .replace(/Tassements/g, "Settlement")
    .replace(/Écoulement.*[Pp]entes/g, "Seepage Analysis")
    // ── Noms d'onglets KB ──────────────────────────────────────────
    .replace(/Utilité du Module/g, "Module Overview")
    .replace(/Géométrie des blocs/g, "Block Geometry")
    .replace(/Géométrie/g, "Geometry")
    .replace(/Profil terrain \+ Sols/g, "Ground Profile + Soils")
    .replace(/Surcharges? & Terrain/g, "Surcharges & Ground")
    .replace(/Surcharges? & Nappe/g, "Surcharges & Groundwater")
    .replace(/Nappe & Drainage/g, "Groundwater & Drainage")
    .replace(/Nappe phréatique/g, "Groundwater")
    .replace(/Nappe \+ Substratum/g, "Groundwater + Substratum")
    .replace(/Phase de calcul/g, "Calculation phase")
    .replace(/Vérifications & FS/g, "Checks & SF")
    .replace(/Vérifications & Résultats/g, "Checks & Results")
    .replace(/Vérifications/g, "Checks")
    .replace(/Phases d'excavation/g, "Excavation phases")
    .replace(/Ferraillage EC2/g, "Reinforcement EC2")
    .replace(/Modèles de solide/g, "Rigid bodies")
    .replace(/Assignation/g, "Assignment")
    .replace(/Surcharge/g, "Surcharge")
    .replace(/Couches de sol/g, "Soil layers")
    .replace(/Ancrages \/ Butons/g, "Anchors / Struts")
    .replace(/Analyse \+ Séisme/g, "Analysis + Seismic")
    .replace(/Capacité portante \/ Tassement \/ Dimensionnement/g, "Bearing capacity / Settlement / Design")
    .replace(/Base de fond\. \/ Lit de sable/g, "Foundation base / Sand bed")
    .replace(/Données CPT/g, "CPT Data")
    .replace(/Tronçons/g, "Sections")
    .replace(/Coupe de synthèse/g, "Synthesis cross-section")
    .replace(/Discontinuités/g, "Discontinuities")
    .replace(/Calcul & SF/g, "Calculation & SF")
    .replace(/Paramètres/g, "Parameters")
    .replace(/Matériaux/g, "Materials")
    .replace(/Résultats/g, "Results")
    .replace(/Séisme \(Mononobe-Okabe\)/g, "Seismic (Mononobe-Okabe)")
    .replace(/\bSéisme\b/g, "Seismic")
    .replace(/\bSols\b/g, "Soils")
    .replace(/\bProjet\b/g, "Project")
    .replace(/\bCharge\b/g, "Load")
    .replace(/\bCharges? groupe/g, "Group loads")
    .replace(/\bSondages\b/g, "Boreholes")
    .replace(/Sols & Nappes/g, "Soils & Groundwater")
    .replace(/Structure & Géométrie/g, "Structure & Geometry")
    .replace(/Renforcement/g, "Reinforcement")
    .replace(/Types de renforcement/g, "Reinforcement types")
    .replace(/Types de clous/g, "Nail types")
    .replace(/Types de treillis \/ Béton projeté/g, "Mesh / Shotcrete")
    .replace(/Stabilité & Résultats/g, "Stability & Results")
    .replace(/Terrains/g, "Ground conditions")
    .replace(/Fondation & Fondation ancrée \(Bêche\)/g, "Foundation & Key (Anti-slide key)")
    .replace(/Matériau \/ Remblai \/ Sols/g, "Material / Fill / Soils");
}

function adaptFrance(texte, pays) {
  if (!texte || pays !== "france") return texte;
  return texte
    .replace(/RPA 2024/g, "EC8 (NF EN 1998)")
    .replace(/\bRPA\b/g, "EC8")
    .replace(/DTR BC 2\.33\.?3?/g, "NF P 94-281")
    .replace(/DTR BA 2\.41/g, "EC2 / BAEL")
    .replace(/DTR/g, "EC7/EC8")
    .replace(/S1–S4|S1\/S2\/S3\/S4/g, "Classes sol A–E (EC8 §3.1)")
    .replace(/zone[s]? (?:≥ )?IIb[, ]*III[, ]*IV/g, "zones ≥ 3 EC8")
    .replace(/zone[s]? III→VI/g, "zones 3→5 EC8")
    .replace(/zone[s]? ≥ IIb/g, "zone ≥ 3 EC8")
    .replace(/zone[s]? ≥ IIa/g, "zone ≥ 2 EC8")
    .replace(/Zone IIb/g, "Zone 3 EC8")
    .replace(/Zone IIa/g, "Zone 2 EC8")
    .replace(/Zone III/g, "Zone 3 EC8")
    .replace(/Zone IV/g, "Zone 4 EC8")
    .replace(/[Kk]h = A × η \/ 2/g, "kh = agR·S/2g (NF EN 1998-5)")
    .replace(/SF_min sismique/g, "FS_min sismique (EC8)")
    .replace(/SF_min/g, "FS_min (EC8)")
    .replace(/Kv = Kh \/ 2/g, "kv = kh/2 (EC8 §7.3.2.2)")
    .replace(/EN 1997-1 DA2 \+ vérif\. RPA 2024/g, "EC7 Approche 2 (NF EN 1997-1)")
    .replace(/option 1.*Standard.*coefficients de sécurité/gi, "Approche 2 EC7")
    .replace(/SFo_sismique ≥ 1\.30/g, "GEO ≥ 1.0 (EC7 A2)")
    .replace(/SFs_sismique ≥ 1\.25/g, "GEO glissement ≥ 1.0 (EC7)")
    .replace(/SFb_sismique ≥ 1\.00/g, "GEO portance ≥ 1.0 (EC7)")
    .replace(/FS portance sismique ≥ 2\.0/g, "GEO portance ≥ 1.0 EC7 A2")
    .replace(/abaque RPA 2024/g, "corrélation pressiomètre NF P 94-261")
    .replace(/SF sismique ≥ 1\.10/g, "FS sismique ≥ 1.10 (NF EN 1998-5)")
    .replace(/ \(Tell algérien[^)]*\)/g, "")
    .replace(/,\s*Tell algérien\b[^,.)]*/g, "")
    .replace(/ \(piémont atlasique[^)]*\)/g, "")
    .replace(/,\s*piémont atlasique\b/g, "")
    .replace(/ \(Chlef,?\s*Médéa,?\s*Saïda\)/g, "");
}

// LOT 10L — Grande Bascule : plus de whitelist, backend exclusif en cockpit
const _API = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Convertit un module backend (guidageInjecte.modules[*]) en objet COPILOTE_KB-compatible.
// Les onglets sont construits depuis les onglet_geo5 des guidance_etapes (même clé
// que _etapesActif utilise pour filtrer) → injection fonctionne sans changement.
function _buildModFromBackend(bm) {
  const etapes = bm.guidance_etapes || [];
  const seen = new Map();
  let counter = 1;
  etapes.forEach(e => {
    const key = e.onglet_geo5 || `Étape ${e.etape}`;
    if (!seen.has(key)) seen.set(key, { num: counter++, id: `bk${counter}` });
  });
  const onglets = Array.from(seen.entries()).map(([nom, { num, id }]) => ({
    nom, num, id, resume: "", checklist: [], utilite: false,
  }));
  if (onglets.length === 0) onglets.push({
    nom: "Guidage", num: 1, id: "bk_na", resume: "", checklist: [], utilite: false,
  });
  return {
    nom:          bm.nom || bm.module_id || "Module",
    couleur:      "#4ade80",
    icone:        "⚙️",
    fichier_demo: bm.fichier_exemple || "",
    description:  bm.resume || "",
    onglets,
  };
}

const NIVEAU_DARK = {
  "TRÈS_ADAPTÉE":          { label: "Très adaptée",     color: "#4ade80", bg: "rgba(22,163,74,0.14)" },
  "ADAPTÉE":               { label: "Adaptée",           color: "#60a5fa", bg: "rgba(37,99,235,0.12)" },
  "ADAPTÉE_AVEC_RÉSERVES": { label: "Avec réserves",     color: "#fbbf24", bg: "rgba(251,191,36,0.10)" },
  "PEU_ADAPTÉE":           { label: "Peu adaptée",       color: "#f87171", bg: "rgba(220,38,38,0.10)" },
  "COMPLÉMENTAIRE":        { label: "Complémentaire",    color: "#94a3b8", bg: "rgba(71,85,105,0.12)" },
};

function TabCoPilote({ donneesProjet, hasCopilote, copiloteMode, pays: paysProp, kbSource = "frontend", userId, onRetenirSolution, diagnosticBackend }) {
  const { t } = useTranslation();
  // Priorité : prop pays (App.js) > clientInfo.pays > zone sismique format > défaut algérie
  const pays = paysProp
    || donneesProjet?.clientInfo?.pays
    || (["1","2","3","4","5"].includes(String(donneesProjet?.zoneSismique)) ? "france" : "algerie");
  const af = (texte) => pays === "eu" ? adaptEU(texte) : adaptFrance(texte, pays);

  // Annexe nationale — injecte valeurs pays dans la KB (exemples, FS, normes)
  const paysCode = pays === "france" ? "FR" : pays?.toUpperCase() || "FR";
  const { annexe } = useAnnexe(paysCode);
  const COPILOTE_KB = useMemo(() => buildCopiloteKB(annexe), [annexe]);

  // Modules recommandés par la solution
  // Priorité : mapping déclaratif SOLUTION_VERS_COPILOTE[s.id] (Co-Pilote-1)
  // Fallback  : pipeline legacy modules_geo5 strings → NOM_VERS_CLE
  // FIX-RANK.d : donneesProjet.solutions reçoit les solutions BACKEND via onSolutions()
  // (propagé par FIX-RANK.b depuis PageSolution.jsx) — aucun changement structurel requis.
  const modulesRecommandes = useMemo(() => {
    if (!donneesProjet?.solutions) return [];
    const keys = donneesProjet.solutions.flatMap(s => {
      if (s.id && SOLUTION_VERS_COPILOTE[s.id]) return SOLUTION_VERS_COPILOTE[s.id];
      return (s.modules_geo5 || []).map(nom => NOM_VERS_CLE[nom]).filter(Boolean);
    });
    return [...new Set(keys)].filter(Boolean);
  }, [donneesProjet]);

  // Alertes projet-spécifiques du backend (par module)
  const alertesParModule = useMemo(() => {
    const alertes = donneesProjet?.alertes_projet || [];
    const map = {};
    alertes.forEach(a => {
      (a.modules || []).forEach(modKey => {
        if (!map[modKey]) map[modKey] = [];
        map[modKey].push(a);
      });
    });
    return map;
  }, [donneesProjet]);

  const aAnalyse = !!donneesProjet?.solutions?.length;
  const modeSécurité = !!donneesProjet?.mode_securite;

  // ── Solution retenue (10a/10a-bis) ──────────────────────────────────────────
  const solutionRetenue = donneesProjet?.solution_retenue || null;
  const screenMode = solutionRetenue ? "cockpit" : "selection";

  const retenuSolution = useMemo(() =>
    donneesProjet?.solutions?.find(s => s.id === solutionRetenue?.id) || null,
    [donneesProjet?.solutions, solutionRetenue?.id]
  );

  // Normalise SOLUTION_VERS_COPILOTE (string ou tableau) → toujours un tableau
  // Fallback : modules_geo5 du catalogue peut être [{module_key, label_fr}] (objets backend)
  //            ou ["Nom string"] (format legacy)
  const _solMods = (sol) => {
    const raw = sol?.id ? SOLUTION_VERS_COPILOTE[sol.id] : null;
    if (raw) return Array.isArray(raw) ? raw : [raw];
    return (sol?.modules_geo5 || []).map(n => {
      if (typeof n === "string") return NOM_VERS_CLE[n];
      // Objet {module_key, label_fr} depuis solutions_catalogue.py
      return MODULE_KEY_VERS_CLE[n.module_key]
        ?? NOM_VERS_CLE[n.label_fr?.replace(/^GEO5\s+/i, "")]
        ?? null;
    }).filter(Boolean);
  };

  const modulesRetenus = useMemo(() => _solMods(retenuSolution), [retenuSolution]); // eslint-disable-line react-hooks/exhaustive-deps

  const [candidat, setCandidat] = useState(null);

  // ── Guidage injecté (10e) ───────────────────────────────────────────────────
  const [guidageInjecte,   setGuidageInjecte]   = useState(null);
  const [guidageLoading,   setGuidageLoading]   = useState(false);
  const [guidageError,     setGuidageError]     = useState(null);

  const _persistRetenue = (retenue) => {
    onRetenirSolution?.(retenue);
    const projetId = donneesProjet?._projetId;
    if (!projetId || !userId) return;
    const storageKey = `geo5_projets_${userId}`;
    const projets = JSON.parse(localStorage.getItem(storageKey) || "[]");
    const idx = projets.findIndex(p => p.id === String(projetId) || p.donneesProjet?._projetId === projetId);
    if (idx >= 0) {
      projets[idx].donneesProjet = { ...projets[idx].donneesProjet, solution_retenue: retenue };
      localStorage.setItem(storageKey, JSON.stringify(projets));
    }
  };

  const handleRetenirCopilote = (sol) => {
    const recommandeeId = donneesProjet?.solutions?.[0]?.id || null;
    const retenue = { id: sol.id, label: sol.nom, date_iso: new Date().toISOString(), recommandee: recommandeeId, ecart_motif: "" };
    _persistRetenue(retenue);
    setCandidat(null);
  };

  const handleChangerSolution = () => {
    _persistRetenue(null);
  };

  // Toujours afficher tous les modules — recommandés en premier
  // En mode cockpit : uniquement les modules retenus
  const modulesTries = useMemo(() => {
    // LOT 10L — cockpit : liste pilotée par guidageInjecte (backend exclusif)
    if (screenMode === "cockpit" && guidageInjecte?.modules?.length > 0) {
      const backendEntries = guidageInjecte.modules.map(m => [
        m.kb_key || m.module_id,
        _buildModFromBackend(m),
      ]);
      // Sidebar GEO5 réels : ajouter les modules COPILOTE_KB de la solution (ex : amélioration)
      // qui ne sont pas déjà servis par le guidage backend (pseudo-module uniquement)
      const backendKeys = new Set(backendEntries.map(([k]) => k));
      const extras = modulesRetenus
        .filter(k => !backendKeys.has(k) && COPILOTE_KB[k])
        .map(k => [k, COPILOTE_KB[k]]);
      return [...backendEntries, ...extras];
    }
    // Cockpit en attente du guidage OU mode sélection → COPILOTE_KB statique
    if (screenMode === "cockpit" && modulesRetenus.length > 0) {
      return Object.entries(COPILOTE_KB).filter(([k]) => modulesRetenus.includes(k));
    }
    const all = Object.entries(COPILOTE_KB);
    const rec = all.filter(([k]) => modulesRecommandes.includes(k));
    const autres = all.filter(([k]) => !modulesRecommandes.includes(k));
    return [...rec, ...autres];
  }, [screenMode, guidageInjecte, modulesRecommandes, modulesRetenus, COPILOTE_KB]);

  // Un module est "déverrouillé" selon le mode :
  // - cockpit : tous les modules retenus sont accessibles
  // - "complet" : tous les modules accessibles
  // - "solution" : uniquement les modules de la solution analysée
  const isDebloque = (key) => {
    if (screenMode === "cockpit") return true; // LOT 10L : tous les modules backend accessibles
    if (copiloteMode === "complet" && hasCopilote) return true;
    return modulesRecommandes.includes(key) || !aAnalyse;
  };

  // LOT 10L : fallback sur premier module COPILOTE_KB — jamais "stabilite_pentes" hardcodé
  const premiereClé = modulesTries.find(([k]) => isDebloque(k))?.[0]
    || modulesTries[0]?.[0]
    || Object.keys(COPILOTE_KB)[0];
  const [moduleActif, setModuleActif] = useState(premiereClé);
  const [ongletActif, setOngletActif] = useState(0);
  const [checklistState, setChecklistState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('geo5_checklist_global') || '{}'); }
    catch { return {}; }
  });
  // backendMods supprimé (LOT 10L — plus de fetch /copilote/kb par module)
  const [checklistCriteres, setChecklistCriteres] = useState(() => {
    try { return JSON.parse(localStorage.getItem('geo5_checklist_criteres') || '{}'); }
    catch { return {}; }
  });

  // Checklist persistence
  useEffect(() => {
    if (Object.keys(checklistState).length > 0)
      localStorage.setItem('geo5_checklist_global', JSON.stringify(checklistState));
  }, [checklistState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (Object.keys(checklistCriteres).length > 0)
      localStorage.setItem('geo5_checklist_criteres', JSON.stringify(checklistCriteres));
  }, [checklistCriteres]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guidage injecté — module correspondant au moduleActif courant
  const _normalOnglet = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const _guidageModActif = useMemo(() => {
    if (!guidageInjecte?.modules) return null;
    return guidageInjecte.modules.find(m =>
      m.kb_key === moduleActif ||
      m.module_id?.replace(/^\d+_/, '') === moduleActif ||
      m.module_id === moduleActif
    ) || null;
  }, [guidageInjecte, moduleActif]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync moduleActif when entering cockpit (legacy : modulesRetenus COPILOTE_KB)
  useEffect(() => {
    if (screenMode === "cockpit" && modulesRetenus.length > 0 && !modulesRetenus.includes(moduleActif)) {
      setModuleActif(modulesRetenus[0]);
      setOngletActif(0);
    }
  }, [screenMode, modulesRetenus.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // LOT 10L — sync moduleActif vers la clé backend quand guidageInjecte arrive
  useEffect(() => {
    if (screenMode !== "cockpit" || !guidageInjecte?.modules?.length) return;
    const backendKeys = guidageInjecte.modules.map(m => m.kb_key || m.module_id);
    if (!backendKeys.includes(moduleActif)) {
      setModuleActif(backendKeys[0]);
      setOngletActif(0);
    }
  }, [guidageInjecte?.modules?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch guidage injecté à l'entrée du cockpit (10e) ──────────────────────
  useEffect(() => {
    if (screenMode !== "cockpit" || !solutionRetenue?.id) {
      setGuidageInjecte(null);
      setGuidageError(null);
      return;
    }
    setGuidageInjecte(null);
    setGuidageError(null);
    setGuidageLoading(true);

    const token = localStorage.getItem("geo5_token");

    // Reconstruit sol_detail depuis diagnosticBackend (préféré) ou donneesProjet.couches
    const solDetail = diagnosticBackend?.sol_detail || {
      rdt: donneesProjet?.rdt != null ? parseFloat(donneesProjet.rdt) : undefined,
      couches: (donneesProjet?.couches || []).map(c => ({
        nature:          c.nature || c.nom || "",
        phi:             c.phi != null ? parseFloat(c.phi) : undefined,
        cohesion:        c.cohesion != null ? parseFloat(c.cohesion) : undefined,
        gamma:           c.gamma != null ? parseFloat(c.gamma) : undefined,
        profondeur_toit: c.profondeur_toit,
        profondeur_base: c.profondeur_base,
      })),
    };

    const body = {
      pays: paysCode,    // routage KB pays (LOT 10h — FR_* prioritaire pour France)
      diagnostic: {
        sol_detail: solDetail,
        rapport:    diagnosticBackend?.rapport || {},
      },
      projet: {
        hauteur:       donneesProjet?.hauteur       ? parseFloat(donneesProjet.hauteur)   : undefined,
        surcharge:     donneesProjet?.surcharge     ? parseFloat(donneesProjet.surcharge) : undefined,
        zone_sismique: donneesProjet?.zoneSismique  || undefined,
        rdt:           donneesProjet?.rdt           ? parseFloat(donneesProjet.rdt)       : undefined,
        couches:       (donneesProjet?.couches || []).map(c => ({ ...c })),
      },
    };

    fetch(`${_API}/copilote/guidage/${solutionRetenue.id}`, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
      .then(r => {
        if (!r.ok) return Promise.reject(`Erreur réseau ${r.status}`);
        return r.json();
      })
      .then(data => { setGuidageInjecte(data); setGuidageLoading(false); })
      .catch(err => { setGuidageError(String(err)); setGuidageLoading(false); });
  }, [screenMode, solutionRetenue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // LOT 10L — cockpit : mod depuis guidageInjecte si le module est backend
  // Si moduleActif est un module COPILOTE_KB appended (ex : tassements depuis amélioration)
  // → utiliser COPILOTE_KB directement (pas de données guidage pour ce module)
  const mod = (() => {
    if (screenMode === "cockpit" && guidageInjecte?.modules?.length > 0) {
      const bm = guidageInjecte.modules.find(m =>
        (m.kb_key || m.module_id) === moduleActif
      );
      if (bm) return _buildModFromBackend(bm);
      if (COPILOTE_KB[moduleActif]) return COPILOTE_KB[moduleActif];
      return _buildModFromBackend(guidageInjecte.modules[0]);
    }
    // Sélection ou cockpit en chargement → COPILOTE_KB (jamais undefined)
    return COPILOTE_KB[moduleActif] || Object.values(COPILOTE_KB)[0];
  })();
  const ongletData = mod.onglets[ongletActif];

  // étapes injectées pour l'onglet actif (cockpit uniquement)
  const _etapesActif = (screenMode === "cockpit" && ongletData && !ongletData.utilite
    && _guidageModActif?.guidage_disponible)
    ? (_guidageModActif.guidance_etapes || []).filter(
        e => _normalOnglet(e.onglet_geo5) === _normalOnglet(ongletData.nom)
      )
    : [];

  // Phase B/C — détection onglet résultats + critères de vérification
  const _phaseBC = screenMode === "cockpit" && ongletData && _guidageModActif?.phase_calcul &&
    _normalOnglet(ongletData.nom) === _normalOnglet(_guidageModActif.phase_calcul.onglet_kb);
  const _pcCriteres = _guidageModActif?.resultats_criteres || [];
  const _pbKey = `${moduleActif}_phase_b`;
  const _pbDone = checklistCriteres[_pbKey] ? 1 : 0;
  const _pbTotal = _guidageModActif?.phase_calcul ? 1 : 0;
  const _pcDone = _pcCriteres.filter(c => checklistCriteres[`${moduleActif}_${c.id}`]).length;
  const _pcTotal = _pcCriteres.length;

  const toggleCheck = (id, idx) => {
    const key = `${id}_${idx}`;
    setChecklistState(s => ({ ...s, [key]: !s[key] }));
  };

  const checklistDone = (id) => {
    const ong = mod.onglets.find(o => o.id === id);
    if (!ong || !ong.checklist) return 0;
    return ong.checklist.filter((_, idx) => checklistState[`${id}_${idx}`]).length;
  };

  const progressModule = () => {
    const totalA = mod.onglets.reduce((s, o) => s + (o.checklist?.length || 0), 0);
    const doneA = mod.onglets.reduce((s, o) => s + checklistDone(o.id), 0);
    const total = totalA + _pbTotal + _pcTotal;
    const done = doneA + _pbDone + _pcDone;
    return total > 0 ? Math.round((done / total) * 100) : 0;
  };

  const couleur = mod.couleur;

  // ════════════════════════════════════════════════════════════════════════════
  // ÉCRAN 1 — SÉLECTION (si aucune solution_retenue)
  // ════════════════════════════════════════════════════════════════════════════
  if (screenMode === "selection") {
    const solutions = donneesProjet?.solutions || [];
    const grpTA = solutions.filter(s => s.niveau === "TRÈS_ADAPTÉE");
    const grpA  = solutions.filter(s => s.niveau === "ADAPTÉE");
    const grpAar = solutions.filter(s => !["TRÈS_ADAPTÉE", "ADAPTÉE"].includes(s.niveau));

    const renderSelCard = (s) => {
      const cfg = NIVEAU_DARK[s.niveau] || NIVEAU_DARK["COMPLÉMENTAIRE"];
      const isCand = candidat?.id === s.id;
      const mods = _solMods(s);
      return (
        <div key={s.id} style={{
          background: isCand ? cfg.bg : "rgba(255,255,255,0.03)",
          border: `1.5px solid ${isCand ? cfg.color : "rgba(255,255,255,0.08)"}`,
          borderRadius: 12, padding: "14px 16px",
          boxShadow: isCand ? `0 0 0 2px ${cfg.color}40` : "none",
          cursor: "pointer", transition: "all 0.15s",
        }} onClick={() => setCandidat(isCand ? null : s)}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#e2e8f0" }}>{s.nom}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, border: `1px solid ${cfg.color}`, borderRadius: 4, padding: "2px 7px" }}>
              {cfg.label}
            </span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
            {mods.map(m => (
              <span key={m} style={{ background: "rgba(255,255,255,0.06)", color: "#94a3b8", borderRadius: 99, padding: "2px 9px", fontSize: 10, fontWeight: 600 }}>
                {COPILOTE_KB[m]?.nom || m}
              </span>
            ))}
          </div>
          <button style={{
            background: isCand ? cfg.color : "transparent",
            border: `1px solid ${isCand ? cfg.color : "rgba(255,255,255,0.15)"}`,
            color: isCand ? "#fff" : "#94a3b8",
            borderRadius: 6, padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
          }}>
            {isCand ? "✓ Sélectionné" : "Choisir"}
          </button>
        </div>
      );
    };

    const renderGroupe = (label, color, items) => items.length === 0 ? null : (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: `${color}18`, border: `1px solid ${color}40`, borderRadius: 99, padding: "3px 12px", marginBottom: 10, fontSize: 11, fontWeight: 700, color }}>
          {label}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {items.map(renderSelCard)}
        </div>
      </div>
    );

    return (
      <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ color: "#e2e8f0", fontWeight: 800, fontSize: 22, margin: "0 0 6px" }}>
            Quelle solution retenez-vous pour ce projet ?
          </h2>
          <p style={{ color: "#4ade80", fontSize: 13, margin: 0, opacity: 0.75 }}>
            Choisissez la solution pour laquelle vous souhaitez un guidage GEO5 étape par étape.
          </p>
        </div>

        {!aAnalyse ? (
          <div style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 10, padding: "16px 20px", color: "#fbbf24", fontSize: 13 }}>
            ⚠️ Lancez d'abord une analyse pour obtenir la liste des solutions adaptées à votre projet.
          </div>
        ) : (
          <>
            {renderGroupe("Très adaptées", "#4ade80", grpTA)}
            {renderGroupe("Adaptées", "#60a5fa", grpA)}
            {renderGroupe("Avec réserves / peu adaptées", "#fbbf24", grpAar)}

            {candidat && (() => {
              const mods = _solMods(candidat);
              return (
                <div style={{ marginTop: 24, background: "rgba(74,222,128,0.08)", border: "2px solid rgba(74,222,128,0.4)", borderRadius: 14, padding: "18px 20px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#4ade80", marginBottom: 6 }}>
                    {candidat.nom} — {mods.length} module{mods.length > 1 ? "s" : ""} GEO5
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                    {mods.map((m, i) => (
                      <span key={m} style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", borderRadius: 99, padding: "3px 11px", fontSize: 11, fontWeight: 700 }}>
                        {i + 1}. {COPILOTE_KB[m]?.nom || m}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => handleRetenirCopilote(candidat)}
                    style={{ background: "#4ade80", border: "none", color: "#0f172a", borderRadius: 8, padding: "10px 24px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}
                  >
                    Valider et démarrer le guidage →
                  </button>
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: "100%", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }}>
      {/* Header cockpit */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
          <h2 style={{ color: "#e2e8f0", fontWeight: 800, fontSize: 22, margin: 0 }}>
            Copilote GEO5 — {solutionRetenue?.label || ""}
            {(() => {
              const cfg = NIVEAU_DARK[retenuSolution?.niveau] || null;
              return cfg ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, marginLeft: 10, border: `1px solid ${cfg.color}`, borderRadius: 4, padding: "2px 8px", verticalAlign: "middle" }}>
                  {cfg.label}
                </span>
              ) : null;
            })()}
          </h2>
          <button
            onClick={handleChangerSolution}
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#94a3b8", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
          >
            ↩ Changer de solution
          </button>
        </div>
        {/* LOT 10L : header nav depuis backend en cockpit, COPILOTE_KB en attente */}
        {(guidageInjecte?.modules || modulesRetenus).length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {(guidageInjecte?.modules
              ? guidageInjecte.modules.map(m => ({ key: m.kb_key || m.module_id, nom: m.nom || m.kb_key || m.module_id }))
              : modulesRetenus.map(m => ({ key: m, nom: COPILOTE_KB[m]?.nom || m }))
            ).map(({ key, nom }, i, arr) => (
              <span key={key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button
                  onClick={() => { setModuleActif(key); setOngletActif(0); }}
                  style={{
                    background: moduleActif === key ? "#4ade80" : "rgba(74,222,128,0.1)",
                    border: `1px solid ${moduleActif === key ? "#4ade80" : "rgba(74,222,128,0.25)"}`,
                    color: moduleActif === key ? "#0f172a" : "#4ade80",
                    borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  {i + 1}/{arr.length} {nom}
                </button>
                {i < arr.length - 1 && <span style={{ color: "rgba(255,255,255,0.2)", fontSize: 12 }}>→</span>}
              </span>
            ))}
          </div>
        )}
        <p style={{ color: "#4ade80", fontSize: 13, margin: "8px 0 0", opacity: 0.65 }}>
          Guidage étape par étape dans GEO5 — modules de votre solution
        </p>
        {modeSécurité && (
          <div style={{ marginTop:12, background:"rgba(220,38,38,0.15)", border:"2px solid #DC2626", borderRadius:10, padding:"10px 16px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:18, flexShrink:0 }}>⚠️</span>
            <div>
              <span style={{ color:"#FCA5A5", fontWeight:800, fontSize:13 }}>MODE SÉCURITÉ ACTIF</span>
              <span style={{ color:"#FCA5A5", fontSize:12, marginLeft:8 }}>Paramètres de calcul limités aux valeurs minimales sécuritaires — Classe de site S4 — Aucun essai géotechnique renseigné</span>
            </div>
          </div>
        )}
        {(pays === "france" || pays === "eu") && (
          <div style={{ marginTop:12, background:"rgba(21,101,192,0.15)", border:"1.5px solid rgba(21,101,192,0.4)", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:18 }}>{pays === "eu" ? "🇪🇺" : "🇫🇷"}</span>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:"#93c5fd", marginBottom:2 }}>
                {pays === "eu" ? t("copilote_tab.mode_eu_titre") : t("copilote_tab.mode_france_titre")}
              </div>
              <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.5 }}>
                {pays === "eu" ? t("copilote_tab.mode_eu_desc") : t("copilote_tab.mode_france_desc")}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "220px minmax(0,1fr)", gap: 16, alignItems: "start", width: "100%", boxSizing: "border-box", overflow: "hidden" }}>

        {/* ── PANNEAU GAUCHE — Sélection module ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 10, color: "#4ade80", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4, opacity: 0.7 }}>
            {screenMode === "cockpit" ? "Modules GEO5 de votre solution" : hasCopilote ? t("copilote_tab.modules_acces_complet") : t("copilote_tab.modules_label")}
          </div>
          {aAnalyse && !hasCopilote && (
            <div style={{ fontSize:10, color:"#fbbf24", fontWeight:600, padding:"4px 8px", background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.2)", borderRadius:6, marginBottom:4 }}>
              {t("copilote_tab.modules_actifs", { n: modulesRecommandes.length })}
            </div>
          )}
          {modulesTries.map(([key, m], idx) => {
            const isRec = modulesRecommandes.includes(key);
            const debloque = isDebloque(key);
            const isActive = moduleActif === key && debloque;
            const showSep = aAnalyse && modulesRecommandes.length > 0 && idx === modulesRecommandes.length;
            return (
              <div key={key}>
                {showSep && !hasCopilote && (
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", fontWeight:600, padding:"8px 4px 4px", letterSpacing:1, display:"flex", alignItems:"center", gap:6 }}>
                    <span>{t("copilote_tab.modules_non_inclus")}</span>
                  </div>
                )}
                <button
                  onClick={() => { if (debloque) { setModuleActif(key); setOngletActif(0); } }}
                  title={!debloque ? t("copilote_tab.contact_verrou") : undefined}
                  style={{
                    width:"100%",
                    background: isActive ? m.couleur : isRec ? "rgba(251,191,36,0.08)" : "rgba(255,255,255,0.02)",
                    border: `1.5px solid ${isActive ? m.couleur : isRec ? "#fbbf24" : "rgba(255,255,255,0.05)"}`,
                    borderRadius: 10, padding: "11px 14px",
                    cursor: debloque ? "pointer" : "not-allowed",
                    textAlign: "left", transition: "all 0.2s",
                    color: isActive ? "#fff" : debloque ? "#94a3b8" : "rgba(255,255,255,0.2)",
                    opacity: debloque ? 1 : 0.45,
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18, filter: debloque ? "none" : "grayscale(1)" }}>{debloque ? m.icone : "🔒"}</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, display:"flex", alignItems:"center", gap:6 }}>
                        {af(m.nom)}
                        {isRec && !isActive && <span style={{ fontSize:9, background:"#fbbf24", color:"#000", borderRadius:4, padding:"1px 5px", fontWeight:800 }}>⭐</span>}
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.75, marginTop: 1 }}>{m.onglets.length} {t("copilote_tab.onglets_geo5")}</div>
                    </div>
                  </div>
                {isActive && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 3, color: "rgba(255,255,255,0.7)" }}>
                      <span>{t("copilote_tab.progression")}</span>
                      <span>{progressModule()}%</span>
                    </div>
                    <div style={{ height: 4, background: "rgba(255,255,255,0.2)", borderRadius: 99 }}>
                      <div style={{ width: `${progressModule()}%`, height: "100%", background: "#fff", borderRadius: 99, transition: "width 0.4s" }} />
                    </div>
                    {_guidageModActif?.phase_calcul && (
                      <div style={{ display: "flex", gap: 3, marginTop: 5 }}>
                        {[
                          { label: "A", done: mod.onglets.reduce((s, o) => s + checklistDone(o.id), 0), total: mod.onglets.reduce((s, o) => s + (o.checklist?.length || 0), 0), color: "#4ade80" },
                          { label: "B", done: _pbDone, total: _pbTotal, color: "#60a5fa" },
                          { label: "C", done: _pcDone, total: _pcTotal, color: "#f87171" },
                        ].map(p => (
                          <span key={p.label} style={{ fontSize: 9, fontWeight: 700, color: (p.done === p.total && p.total > 0) ? "#fff" : "rgba(255,255,255,0.4)", background: (p.done === p.total && p.total > 0) ? p.color : "rgba(255,255,255,0.1)", borderRadius: 3, padding: "1px 4px" }}>
                            {p.label}{p.total > 0 ? ` ${p.done}/${p.total}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
              </div>
            );
          })}

          {/* Info fichier démo */}
          <div style={{ marginTop: 8, padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(74,222,128,0.15)", borderRadius: 8 }}>
            <div style={{ fontSize: 10, color: "#4ade80", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 5, opacity: 0.7 }}>{t("copilote_tab.fichier_demo")}</div>
            <div style={{ fontSize: 12, color: "#38bdf8", fontWeight: 600 }}>{mod.fichier_demo}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{af(mod.description)}</div>
          </div>

          {/* Message Civisoft si modules verrouillés */}
          {aAnalyse && !hasCopilote && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700, marginBottom: 4 }}>{t("copilote_tab.acces_complet_titre")}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
                {t("copilote_tab.acces_complet_desc")}<br/>
                <span style={{ color: "#4ade80", fontWeight: 600 }}>{t("copilote_tab.acces_complet_contact")}</span><br/>
                <span style={{ color: "#94a3b8" }}>contact@civisoft-fr.com</span><br/>
                <span style={{ color: "#94a3b8" }}>+33 (0)1 XX XX XX XX</span>
              </div>
            </div>
          )}
        </div>

        {/* ── PANNEAU DROIT — Guidage injecté (cockpit) ou KB statique ── */}
        <div style={{ minWidth: 0, overflow: "hidden", width: "100%", boxSizing: "border-box" }}>

          {/* ── LOADING ── */}
          {screenMode === "cockpit" && guidageLoading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: "48px 24px", color: "#4ade80" }}>
              <div style={{ width: 40, height: 40, border: "3px solid rgba(74,222,128,0.2)", borderTopColor: "#4ade80", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: "#94a3b8" }}>Chargement du guidage personnalisé…</div>
            </div>
          )}

          {/* ── ERREUR RÉSEAU (LOT 10L) : 404 → enrichissement, autre → panne réseau ── */}
          {screenMode === "cockpit" && !guidageLoading && guidageError && (() => {
            const is404 = String(guidageError).includes("404");
            return is404 ? (
              <div style={{ background: "rgba(251,191,36,0.08)", border: "1.5px solid rgba(251,191,36,0.3)", borderRadius: 12, padding: "20px 22px", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>⏳</span>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fbbf24" }}>Guidage en cours d'enrichissement</div>
                </div>
                <div style={{ fontSize: 12, color: "#fde68a", lineHeight: 1.7 }}>
                  Le guidage étape par étape pour cette solution sera disponible prochainement.<br/>
                  Consultez la documentation GEO5 et les normes applicables pendant ce délai.
                </div>
              </div>
            ) : (
              <div style={{ background: "rgba(220,38,38,0.1)", border: "1.5px solid rgba(220,38,38,0.35)", borderRadius: 12, padding: "20px 22px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>🔌</span>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f87171" }}>Guidage indisponible — erreur réseau</div>
                </div>
                <div style={{ fontSize: 12, color: "#fca5a5", lineHeight: 1.6, marginBottom: 10 }}>
                  Connexion à l'API de guidage échouée : <code style={{ background: "rgba(0,0,0,0.3)", padding: "1px 6px", borderRadius: 4 }}>{guidageError}</code>
                </div>
                <div style={{ fontSize: 11, color: "#ef4444", fontStyle: "italic" }}>
                  Vérifiez que le backend est démarré, puis rechargez l'onglet Copilote.
                </div>
              </div>
            );
          })()}

          {/* ── ONGLETS + contenu — masqués si erreur backend en mode cockpit ── */}
          {!guidageLoading && !(screenMode === "cockpit" && guidageError) && (
          <>{/* Barre de navigation onglets GEO5 */}
          <div style={{
            display: "flex", gap: 4, overflowX: "auto", paddingBottom: 6, marginBottom: 16, scrollbarWidth: "thin",
            borderBottom: `2px solid ${couleur}30`
          }}>
            {mod.onglets.map((o, i) => {
              const done = checklistDone(o.id);
              const total = o.checklist?.length || 0;
              const complet = done === total && total > 0;
              return (
                <button key={o.id} onClick={() => setOngletActif(i)} style={{
                  background: ongletActif === i ? couleur : "rgba(255,255,255,0.04)",
                  border: `1.5px solid ${ongletActif === i ? couleur : complet ? "#16a34a40" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 8, padding: "6px 12px", cursor: "pointer", whiteSpace: "nowrap",
                  color: ongletActif === i ? "#fff" : complet ? "#4ade80" : "#86efac",
                  fontSize: 12, fontWeight: ongletActif === i ? 700 : 400, transition: "all 0.15s",
                  display: "flex", alignItems: "center", gap: 5,
                }}>
                  {complet && <span style={{ fontSize: 10 }}>✅</span>}
                  <span>{o.num}. {af(o.nom)}</span>
                </button>
              );
            })}
          </div>

          {/* Contenu de l'onglet actif */}
          {ongletData && (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,220px)", gap: 14, minWidth: 0, overflow: "hidden", width: "100%", boxSizing: "border-box" }}>

              {/* Colonne principale */}
              <div style={{ minWidth: 0, overflow: "hidden" }}>
                {/* Titre onglet */}
                <div style={{
                  background: `linear-gradient(135deg, ${couleur}20, ${couleur}08)`,
                  border: `1px solid ${couleur}30`,
                  borderRadius: 12, padding: "14px 16px", marginBottom: 14
                }}>
                  <div style={{ fontSize: 11, color: couleur, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
                    {ongletData.utilite
                      ? t("copilote_tab.guide_label", { nom: mod.nom })
                      : t("copilote_tab.onglet_label", { num: ongletData.num, total: mod.onglets.length, nom: mod.nom })}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0" }}>{af(ongletData.nom)}</div>
                  <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4, wordBreak: "break-word" }}>{af(ongletData.resume)}</div>
                </div>

                {/* ── ALERTES PROJET-SPÉCIFIQUES (backend KB) ── */}
                {(() => {
                  const alertesMod = alertesParModule[moduleActif] || [];
                  if (!alertesMod.length) return null;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                      {alertesMod.map((a, i) => {
                        const isCritique = a.alerte?.startsWith("🔴");
                        const isWarn     = a.alerte?.startsWith("⚠");
                        const bg    = isCritique ? "#450a0a" : isWarn ? "#431407" : "#0c1a2e";
                        const border = isCritique ? "#991b1b" : isWarn ? "#9a3412" : "#1e3a5f";
                        const color  = isCritique ? "#fca5a5" : isWarn ? "#fed7aa" : "#93c5fd";
                        const emoji  = isCritique ? "🚨" : isWarn ? "⚠️" : "ℹ️";
                        return (
                          <div key={i} style={{
                            background: bg, border: `1px solid ${border}`,
                            borderRadius: 10, padding: "11px 14px",
                            display: "flex", gap: 10, alignItems: "flex-start"
                          }}>
                            <span style={{ fontSize: 16, flexShrink: 0 }}>{emoji}</span>
                            <div>
                              <div style={{ fontSize: 11, color: color, fontWeight: 700, opacity: 0.7, marginBottom: 3 }}>
                                {t("copilote_tab.alerte_projet", { id: a.id })}
                              </div>
                              <div style={{ fontSize: 13, color, lineHeight: 1.6 }}>{a.alerte}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* ── RENDU SPÉCIAL : Onglet "Utilité du Module" ── */}
                {ongletData.utilite && (() => {
                  const u = ongletData.utilite;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Description */}
                      <div style={{ background: `${couleur}12`, border: `1px solid ${couleur}30`, borderRadius: 10, padding: "14px 16px" }}>
                        <div style={{ fontSize: 11, color: couleur, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>{t("copilote_tab.description")}</div>
                        <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.7 }}>{af(u.description)}</div>
                      </div>

                      {/* Quand utiliser */}
                      <div style={{ background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 10, padding: "14px 16px" }}>
                        <div style={{ fontSize: 11, color: "#4ade80", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{t("copilote_tab.quand_utiliser")}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                          {u.quand_utiliser.map((item, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                              <span style={{ color: "#4ade80", fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                              <span style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.5 }}>{af(item)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Quand ne pas utiliser */}
                      {u.quand_ne_pas_utiliser && (
                        <div style={{ background: "#431407", border: "1px solid #9a3412", borderRadius: 10, padding: "14px 16px" }}>
                          <div style={{ fontSize: 11, color: "#f87171", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{t("copilote_tab.quand_ne_pas")}</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            {u.quand_ne_pas_utiliser.map((item, i) => (
                              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                <span style={{ color: "#f87171", fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✗</span>
                                <span style={{ fontSize: 13, color: "#fed7aa", lineHeight: 1.5 }}>{af(item)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Modules liés */}
                      <div style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 10, overflow: "hidden" }}>
                        <div style={{ background: "rgba(56,189,248,0.12)", borderBottom: "1px solid rgba(56,189,248,0.2)", padding: "10px 14px", fontSize: 11, color: "#38bdf8", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
                          {t("copilote_tab.modules_lies_titre")}
                        </div>
                        <div style={{ padding: "8px 0" }}>
                          {u.modules_lies.map((ml, i) => (
                            <div key={i} style={{ padding: "10px 14px", borderBottom: i < u.modules_lies.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                              <div style={{ fontSize: 12, color: "#38bdf8", fontWeight: 700, marginBottom: 3 }}>→ {ml.module}</div>
                              <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{af(ml.lien)}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Exemple projet */}
                      {u.exemple_projet && (
                        <div style={{ background: "#0c1a0c", border: "1px solid #166534", borderRadius: 10, padding: "14px 16px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                          <div style={{ fontSize: 13, color: "#bbf7d0", lineHeight: 1.6 }}>
                            <strong style={{ color: "#4ade80" }}>{t("copilote_tab.exemple_concret")} </strong>{af(u.exemple_projet)}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* ── FIN RENDU SPÉCIAL ── */}

                {/* ── Guidage injecté pour cet onglet (cockpit) ── */}
                {/* LOT 10L : guidage_disponible: false → enrichissement (remplace "Guidage de référence") */}
                {screenMode === "cockpit" && !ongletData.utilite && _guidageModActif && !_guidageModActif.guidage_disponible && (
                  <div style={{ background: "rgba(251,191,36,0.08)", border: "1.5px solid rgba(251,191,36,0.3)", borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24", marginBottom: 6 }}>⏳ Guidage en cours d'enrichissement</div>
                    <div style={{ fontSize: 12, color: "#fde68a", lineHeight: 1.7 }}>
                      Le guidage étape par étape pour <strong>{_guidageModActif.nom || "ce module"}</strong> est en cours de préparation.{" "}
                      {_guidageModActif.cahier_technique ? `Référez-vous au cahier GEO5 ${_guidageModActif.cahier_technique} et aux normes applicables.` : "Référez-vous à la documentation GEO5 et aux normes applicables."}
                    </div>
                  </div>
                )}

                {/* ── Encart conformité France DA2* (modules génériques servis en contexte FR) ── */}
                {screenMode === "cockpit" && !ongletData.utilite && _guidageModActif?.conformite_annotation && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.35)", borderRadius: 10, padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                        <span style={{ fontSize: 13 }}>⚠️</span>
                        <span style={{ fontSize: 11, fontWeight: 800, color: "#fbbf24", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          Conformité France DA2* — {_guidageModActif.conformite_annotation.approche}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#fde68a", lineHeight: 1.6, marginBottom: 6 }}>
                        {_guidageModActif.conformite_annotation.alerte}
                      </div>
                      {_guidageModActif.conformite_annotation.coefficients && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 6 }}>
                          {Object.entries(_guidageModActif.conformite_annotation.coefficients).map(([k, v]) => (
                            <span key={k} style={{ fontSize: 10, borderRadius: 5, padding: "3px 9px", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.25)", color: "#fbbf24", fontFamily: "'Courier New', monospace", fontWeight: 700 }}>
                              {k} = {v}
                            </span>
                          ))}
                        </div>
                      )}
                      {_guidageModActif.conformite_annotation.alerte_sismique && (
                        <div style={{ marginTop: 8, background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 7, padding: "8px 12px", fontSize: 11, color: "#fca5a5", lineHeight: 1.5 }}>
                          <span style={{ fontSize: 12, marginRight: 5 }}>🌍</span>
                          {_guidageModActif.conformite_annotation.alerte_sismique}
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: 10, color: "#94a3b8", fontStyle: "italic" }}>
                        {_guidageModActif.conformite_annotation.reference}
                      </div>
                    </div>
                  </div>
                )}

                {_etapesActif.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                    {_etapesActif.map(e => (
                      <div key={e.etape} style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                          <div style={{ background: e.injection ? "#4ade80" : "rgba(100,116,139,0.3)", color: e.injection ? "#0f172a" : "#94a3b8", borderRadius: "50%", width: 24, height: 24, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{e.etape}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0", lineHeight: 1.4 }}>{af(e.action)}</div>
                        </div>
                        <div style={{ background: e.injection ? "rgba(74,222,128,0.08)" : "rgba(255,255,255,0.03)", border: `1px solid ${e.injection ? "rgba(74,222,128,0.25)" : "rgba(255,255,255,0.07)"}`, borderRadius: 8, padding: "8px 12px" }}>
                          <div style={{ fontFamily: e.injection ? "'Courier New', monospace" : "inherit", fontSize: 13, color: e.injection ? "#4ade80" : "#c8d6c8", lineHeight: 1.6, wordBreak: "break-word" }}>
                            {e.valeur_affichee || e.valeur_typique || ""}
                          </div>
                          {e.injection && e.valeurs_injectees && Object.keys(e.valeurs_injectees).length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(74,222,128,0.12)" }}>
                              {Object.entries(e.valeurs_injectees).map(([k, v]) => (
                                <span key={k} style={{ fontSize: 10, borderRadius: 5, padding: "3px 9px", background: "rgba(37,99,235,0.08)", borderLeft: "2px solid rgba(37,99,235,0.35)", color: "#94a3b8" }}>
                                  <span style={{ color: "#93c5fd", fontWeight: 700 }}>{_fmtKey(k)}</span>
                                  <span style={{ color: "#94a3b8" }}> = </span>
                                  <span style={{ color: "#60a5fa", fontWeight: 800 }}>{typeof v.valeur === "number" ? v.valeur : String(v.valeur)}</span>
                                  <span style={{ color: "#94a3b8" }}> · {v.source}</span>
                                </span>
                              ))}
                            </div>
                          )}
                          {!e.injection && (e.valeur_typique || e.valeur_affichee) && (
                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 6, fontStyle: "italic" }}>valeur de référence — à adapter à votre projet</div>
                          )}
                        </div>
                        {e.annotation_contextuelle && (
                          <div style={{ marginTop: 8, background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 8, padding: "8px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <span style={{ fontSize: 13 }}>ℹ️</span>
                            <span style={{ fontSize: 12, color: "#38bdf8", lineHeight: 1.5 }}>{e.annotation_contextuelle}</span>
                          </div>
                        )}
                        {e.alerte && (
                          <div style={{ marginTop: 8, background: "#431407", border: "1px solid #9a3412", borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <span style={{ fontSize: 14 }}>🚨</span>
                            <span style={{ fontSize: 12, color: "#fed7aa", lineHeight: 1.5 }}>{af(e.alerte)}</span>
                          </div>
                        )}
                        {e.detail && <div style={{ marginTop: 8, fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>{af(e.detail)}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Phase B : Lancer l'analyse dans GEO5 ── */}
                {_phaseBC && _guidageModActif.phase_calcul && (
                  <div style={{ background: "rgba(37,99,235,0.08)", border: "1.5px solid rgba(37,99,235,0.35)", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span style={{ background: "rgba(37,99,235,0.2)", color: "#60a5fa", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 800 }}>PHASE B</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#93c5fd" }}>Lancer l'analyse dans GEO5</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#e2e8f0", marginBottom: 8, lineHeight: 1.5 }}>
                      {_guidageModActif.phase_calcul.bouton_geo5}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Ce que GEO5 va calculer :</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {(_guidageModActif.phase_calcul.ce_que_produit || []).map((item, i) => (
                          <span key={i} style={{ fontSize: 11, background: "rgba(37,99,235,0.12)", color: "#93c5fd", borderRadius: 99, padding: "2px 9px" }}>{item}</span>
                        ))}
                      </div>
                    </div>
                    <div
                      onClick={() => setChecklistCriteres(s => ({ ...s, [_pbKey]: !s[_pbKey] }))}
                      style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "7px 10px", borderRadius: 8, background: checklistCriteres[_pbKey] ? "rgba(22,163,74,0.1)" : "transparent", marginTop: 2 }}
                    >
                      <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, border: `2px solid ${checklistCriteres[_pbKey] ? "#16a34a" : "rgba(255,255,255,0.2)"}`, background: checklistCriteres[_pbKey] ? "#16a34a" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {checklistCriteres[_pbKey] && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 12, color: checklistCriteres[_pbKey] ? "#4ade80" : "#cbd5e1" }}>J'ai lancé l'analyse dans GEO5</span>
                    </div>
                  </div>
                )}

                {/* ── Phase C : Checklist de vérification cochable ── */}
                {_phaseBC && _pcCriteres.length > 0 && (
                  <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
                    <div style={{ background: "rgba(220,38,38,0.08)", borderBottom: "1px solid rgba(220,38,38,0.2)", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ background: "rgba(220,38,38,0.2)", color: "#f87171", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 800 }}>PHASE C</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#fca5a5" }}>Vérification des critères</span>
                      </div>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{_pcDone}/{_pcTotal}</span>
                    </div>
                    <div style={{ padding: "6px 4px" }}>
                      {_pcCriteres.map(c => {
                        const ckey = `${moduleActif}_${c.id}`;
                        const checked = !!checklistCriteres[ckey];
                        const seuil = c.seuil_affiche || c.seuil_label || "";
                        return (
                          <div key={c.id} style={{ padding: "2px 4px" }}>
                            <div
                              onClick={() => setChecklistCriteres(s => ({ ...s, [ckey]: !s[ckey] }))}
                              style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 10px", cursor: "pointer", borderRadius: 8, background: checked ? "rgba(22,163,74,0.08)" : "transparent" }}
                            >
                              <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1, border: `2px solid ${checked ? "#16a34a" : "rgba(255,255,255,0.2)"}`, background: checked ? "#16a34a" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {checked && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>✓</span>}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: checked ? "#4ade80" : "#e2e8f0", fontWeight: 600, textDecoration: checked ? "line-through" : "none", lineHeight: 1.4 }}>
                                  {af(c.critere)}{" "}
                                  <span style={{ color: c.injection ? "#4ade80" : "#94a3b8", fontWeight: c.injection ? 700 : 400, fontFamily: c.injection ? "'Courier New', monospace" : "inherit", fontSize: c.injection ? 12 : 11 }}>
                                    {af(seuil)}
                                  </span>
                                </div>
                                {c.norme && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{c.norme}</div>}
                              </div>
                            </div>
                            {!checked && !c.ia_fallback && c.levier && (
                              <div style={{ marginLeft: 38, marginBottom: 4, paddingLeft: 8, borderLeft: "2px solid rgba(251,191,36,0.3)", fontSize: 11, color: "#fbbf24", lineHeight: 1.5 }}>
                                🔧 {af(c.levier)}
                              </div>
                            )}
                            {!checked && c.ia_fallback && (
                              <div style={{ marginLeft: 38, marginBottom: 4, paddingLeft: 8, borderLeft: "2px solid rgba(148,163,184,0.2)", fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                                → Demandez au copilote IA : que faire si {af(c.critere)} insuffisant ?
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ padding: "0 14px 10px" }}>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 99 }}>
                        <div style={{ width: `${_pcTotal > 0 ? (_pcDone / _pcTotal) * 100 : 0}%`, height: "100%", background: "#dc2626", borderRadius: 99, transition: "width 0.3s" }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ALERTE si présente */}
                {!ongletData.utilite && ongletData.alerte && (
                  <div style={{
                    background: "#431407", border: "1px solid #9a3412",
                    borderRadius: 10, padding: "12px 14px", marginBottom: 14,
                    display: "flex", gap: 10, alignItems: "flex-start"
                  }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>🚨</span>
                    <div style={{ fontSize: 13, color: "#fed7aa", lineHeight: 1.6, wordBreak: "break-word", overflowWrap: "break-word" }}>{af(ongletData.alerte)}</div>
                  </div>
                )}

                {/* Ce qu'il faut saisir — masqué si injection présente pour cet onglet */}
                {!ongletData.utilite && ongletData.quoi_saisir && _etapesActif.length === 0 && (
                  <div style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 10, padding: "14px 16px", marginBottom: 14
                  }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>
                      {t("copilote_tab.quoi_saisir_titre")}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {ongletData.quoi_saisir.map((item, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                          <span style={{ color: couleur, fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 1 }}>→</span>
                          <span style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.5 }}>{af(item)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sous-étapes si présentes — masquées si injection présente */}
                {!ongletData.utilite && ongletData.sous_etapes && _etapesActif.length === 0 && (
                  <div style={{
                    background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: 10, padding: "14px 16px", marginBottom: 14
                  }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>
                      {t("copilote_tab.sequence_titre")}
                    </div>
                    {ongletData.sous_etapes.map((step, i) => (
                      <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "flex-start" }}>
                        <div style={{
                          background: couleur, color: "#fff", borderRadius: "50%",
                          width: 20, height: 20, flexShrink: 0, display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 11, fontWeight: 700
                        }}>{i + 1}</div>
                        <span style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5 }}>{af(step)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Conseil expert */}
                {!ongletData.utilite && ongletData.conseil && (
                  <div style={{
                    background: "#0c1a0c", border: "1px solid #166534",
                    borderRadius: 10, padding: "12px 14px", marginBottom: 14,
                    display: "flex", gap: 10, alignItems: "flex-start"
                  }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
                    <div style={{ fontSize: 13, color: "#bbf7d0", lineHeight: 1.6 }}>
                      <strong style={{ color: "#4ade80" }}>{t("copilote_tab.conseil_expert")} </strong>
                      {af(ongletData.conseil)}
                    </div>
                  </div>
                )}

                {/* Navigation précédent / suivant */}
                <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                  <button
                    onClick={() => setOngletActif(Math.max(0, ongletActif - 1))}
                    disabled={ongletActif === 0}
                    style={{
                      flex: 1, background: ongletActif === 0 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.1)", color: ongletActif === 0 ? "#374151" : "#e2e8f0",
                      borderRadius: 8, padding: "10px 16px", cursor: ongletActif === 0 ? "default" : "pointer",
                      fontSize: 13, fontWeight: 600
                    }}
                  >{t("copilote_tab.onglet_precedent")}</button>
                  <button
                    onClick={() => setOngletActif(Math.min(mod.onglets.length - 1, ongletActif + 1))}
                    disabled={ongletActif === mod.onglets.length - 1}
                    style={{
                      flex: 1, background: ongletActif < mod.onglets.length - 1 ? couleur : "rgba(255,255,255,0.03)",
                      border: "none", color: ongletActif < mod.onglets.length - 1 ? "#fff" : "#374151",
                      borderRadius: 8, padding: "10px 16px",
                      cursor: ongletActif < mod.onglets.length - 1 ? "pointer" : "default",
                      fontSize: 13, fontWeight: 700
                    }}
                  >{t("copilote_tab.onglet_suivant")}</button>
                </div>
              </div>

              {/* Colonne droite — valeurs typiques + checklist */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0, overflow: "hidden" }}>

                {/* Paramètres du projet — si couches saisies et onglet Sols */}
                {donneesProjet?.couches?.length > 0 && (ongletData.nom === "Sols" || ongletData.nom === "Couches" || ongletData.quoi_saisir?.some(q => q.toLowerCase().includes("sol") || q.toLowerCase().includes("couche") || q.toLowerCase().includes("γ"))) && (
                  <div style={{ background:"rgba(251,191,36,0.06)", border:"1.5px solid rgba(251,191,36,0.3)", borderRadius:12, overflow:"hidden" }}>
                    <div style={{ background:"rgba(251,191,36,0.15)", borderBottom:"1px solid rgba(251,191,36,0.2)", padding:"10px 14px", fontSize:11, color:"#fbbf24", fontWeight:700, letterSpacing:2, textTransform:"uppercase" }}>
                      {t("copilote_tab.params_projet_titre")}
                    </div>
                    <div style={{ padding:"8px 0" }}>
                      {donneesProjet.couches.map((c, i) => (
                        <div key={i} style={{ padding:"8px 12px", borderBottom: i < donneesProjet.couches.length-1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:4 }}>
                            <span style={{ fontSize:11, color:"#fbbf24", fontWeight:700 }}>{t("copilote_tab.couche_label", { n: i+1, nom: c.nom, nature: c.nature })}</span>
                            <span style={{ fontSize:10, color:"#94a3b8" }}>{c.profondeur_toit}m → {c.profondeur_base}m</span>
                          </div>
                          <div style={{ fontSize:12, color:"#38bdf8", fontWeight:600, marginTop:3 }}>
                            γ = {c.gamma} kN/m³ &nbsp;|&nbsp; c = {c.cohesion} kPa &nbsp;|&nbsp; φ = {c.phi}°
                          </div>
                        </div>
                      ))}
                      {donneesProjet.hauteur && (
                        <div style={{ padding:"8px 12px", borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                          <span style={{ fontSize:11, color:"#fbbf24", fontWeight:700 }}>{t("copilote_tab.h_fouille")}</span>
                          <span style={{ fontSize:12, color:"#38bdf8", fontWeight:600, marginLeft:12 }}>{donneesProjet.hauteur} m</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Valeurs typiques */}
                {ongletData.valeurs_typiques && ongletData.valeurs_typiques.length > 0 && (
                  <div style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12, overflow: "hidden"
                  }}>
                    <div style={{
                      background: `${couleur}20`, borderBottom: `1px solid ${couleur}30`,
                      padding: "10px 14px", fontSize: 11, color: couleur,
                      fontWeight: 700, letterSpacing: 2, textTransform: "uppercase"
                    }}>
                      {t("copilote_tab.valeurs_typiques_titre")} — {pays === "france" ? t("copilote_tab.valeurs_pays_fr") : t("copilote_tab.valeurs_pays_eu")}
                    </div>
                    <div style={{ padding: "8px 0" }}>
                      {ongletData.valeurs_typiques.map((v, i) => (
                        <div key={i} style={{
                          padding: "8px 12px",
                          borderBottom: i < ongletData.valeurs_typiques.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none"
                        }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{af(v.param)}</span>
                            <span style={{ fontSize: 12, color: "#38bdf8", fontWeight: 700, wordBreak: "break-word", overflowWrap: "break-word" }}>{af(v.val)}</span>
                          </div>
                          {v.note && (
                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, wordBreak: "break-word" }}>{af(v.note)}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Checklist avant de passer à l'onglet suivant */}
                {ongletData.checklist && ongletData.checklist.length > 0 && (
                  <div style={{
                    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12, overflow: "hidden"
                  }}>
                    <div style={{
                      background: "rgba(22,163,74,0.2)", borderBottom: "1px solid rgba(22,163,74,0.3)",
                      padding: "10px 14px", fontSize: 11, color: "#4ade80",
                      fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
                      display: "flex", justifyContent: "space-between", alignItems: "center"
                    }}>
                      <span>{t("copilote_tab.checklist_titre")}</span>
                      <span style={{ fontSize: 12 }}>
                        {checklistDone(ongletData.id)}/{ongletData.checklist.length}
                      </span>
                    </div>
                    <div style={{ padding: "8px 4px" }}>
                      {ongletData.checklist.map((item, idx) => {
                        const key = `${ongletData.id}_${idx}`;
                        const checked = !!checklistState[key];
                        return (
                          <div
                            key={idx}
                            onClick={() => toggleCheck(ongletData.id, idx)}
                            style={{
                              display: "flex", alignItems: "flex-start", gap: 10,
                              padding: "7px 10px", cursor: "pointer", borderRadius: 6, margin: "2px 4px",
                              background: checked ? "rgba(22,163,74,0.1)" : "transparent",
                              transition: "background 0.15s"
                            }}
                          >
                            <div style={{
                              width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                              border: `2px solid ${checked ? "#16a34a" : "rgba(255,255,255,0.2)"}`,
                              background: checked ? "#16a34a" : "transparent",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              transition: "all 0.15s"
                            }}>
                              {checked && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800 }}>✓</span>}
                            </div>
                            <span style={{
                              fontSize: 12, color: checked ? "#4ade80" : "#cbd5e1",
                              lineHeight: 1.4, textDecoration: checked ? "line-through" : "none",
                              transition: "all 0.15s"
                            }}>{af(item)}</span>
                          </div>
                        );
                      })}
                    </div>
                    {/* Barre de progression checklist */}
                    <div style={{ padding: "0 14px 10px" }}>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.08)", borderRadius: 99 }}>
                        <div style={{
                          width: `${(checklistDone(ongletData.id) / ongletData.checklist.length) * 100}%`,
                          height: "100%", background: "#16a34a", borderRadius: 99, transition: "width 0.3s"
                        }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Lien inter-modules si dernier onglet Stabilité des Pentes */}
                {moduleActif === "stabilite_pentes" && ongletActif === mod.onglets.length - 1 && (
                  <div style={{
                    background: "rgba(37,99,235,0.1)", border: "1px solid rgba(37,99,235,0.3)",
                    borderRadius: 10, padding: "12px 14px"
                  }}>
                    <div style={{ fontSize: 11, color: "#60a5fa", fontWeight: 700, marginBottom: 6 }}>
                      {t("copilote_tab.lien_stab_titre")}
                    </div>
                    <div style={{ fontSize: 12, color: "#93c5fd", lineHeight: 1.5 }}>
                      {t("copilote_tab.lien_stab_desc")}
                    </div>
                  </div>
                )}

                {/* Lien inter-modules si dernier onglet Murs Cantilever ou Murs Poids */}
                {(moduleActif === "murs_cantilever" || moduleActif === "murs_poids") && ongletActif === mod.onglets.length - 1 && (
                  <div style={{
                    background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.3)",
                    borderRadius: 10, padding: "12px 14px"
                  }}>
                    <div style={{ fontSize: 11, color: "#4ade80", fontWeight: 700, marginBottom: 6 }}>
                      {t("copilote_tab.lien_mur_titre")}
                    </div>
                    <div style={{ fontSize: 12, color: "#bbf7d0", lineHeight: 1.5 }}>
                      {t("copilote_tab.lien_mur_desc")}
                    </div>
                    <button
                      onClick={() => { setModuleActif("stabilite_pentes"); setOngletActif(0); }}
                      style={{
                        marginTop: 8, width: "100%", background: "#16a34a", border: "none",
                        color: "#fff", borderRadius: 6, padding: "8px 12px", cursor: "pointer",
                        fontSize: 12, fontWeight: 700
                      }}
                    >
                      {t("copilote_tab.lien_mur_btn")}
                    </button>
                  </div>
                )}

                {/* Lien pieux → groupe de pieux → tassements */}
                {moduleActif === "pieux" && ongletActif === mod.onglets.length - 1 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    <div style={{ background:"rgba(14,116,144,0.1)", border:"1px solid rgba(14,116,144,0.3)", borderRadius:10, padding:"12px 14px" }}>
                      <div style={{ fontSize:11, color:"#22d3ee", fontWeight:700, marginBottom:6 }}>{t("copilote_tab.lien_pieux_comp_titre")}</div>
                      <div style={{ fontSize:12, color:"#a5f3fc", lineHeight:1.5, marginBottom:8 }}>
                        {t("copilote_tab.lien_pieux_comp_desc")}
                      </div>
                      <button onClick={() => { setModuleActif("groupe_pieux"); setOngletActif(0); }}
                        style={{ width:"100%", background:"#0e7490", border:"none", color:"#fff", borderRadius:6, padding:"7px 12px", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                        {t("copilote_tab.lien_pieux_comp_btn")}
                      </button>
                    </div>
                    <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10, padding:"12px 14px" }}>
                      <div style={{ fontSize:11, color:"#f87171", fontWeight:700, marginBottom:6 }}>{t("copilote_tab.lien_pieux_tass_titre")}</div>
                      <div style={{ fontSize:12, color:"#fca5a5", lineHeight:1.5, marginBottom:8 }}>
                        {t("copilote_tab.lien_pieux_tass_desc")}
                      </div>
                      <button onClick={() => { setModuleActif("tassements"); setOngletActif(0); }}
                        style={{ width:"100%", background:"#dc2626", border:"none", color:"#fff", borderRadius:6, padding:"7px 12px", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                        {t("copilote_tab.lien_pieux_tass_btn")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Lien groupe pieux → tassements */}
                {moduleActif === "groupe_pieux" && ongletActif === mod.onglets.length - 1 && (
                  <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:11, color:"#f87171", fontWeight:700, marginBottom:6 }}>{t("copilote_tab.lien_groupe_titre")}</div>
                    <div style={{ fontSize:12, color:"#fca5a5", lineHeight:1.5, marginBottom:8 }}>
                      {t("copilote_tab.lien_groupe_desc")}
                    </div>
                    <button onClick={() => { setModuleActif("tassements"); setOngletActif(0); }}
                      style={{ width:"100%", background:"#dc2626", border:"none", color:"#fff", borderRadius:6, padding:"7px 12px", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                      {t("copilote_tab.lien_groupe_btn")}
                    </button>
                  </div>
                )}

                {/* Lien micropieux → tassements */}
                {moduleActif === "micropieux" && ongletActif === mod.onglets.length - 1 && (
                  <div style={{ background:"rgba(220,38,38,0.08)", border:"1px solid rgba(220,38,38,0.25)", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:11, color:"#f87171", fontWeight:700, marginBottom:6 }}>{t("copilote_tab.lien_micro_titre")}</div>
                    <div style={{ fontSize:12, color:"#fca5a5", lineHeight:1.5, marginBottom:8 }}>
                      {t("copilote_tab.lien_groupe_desc")}
                    </div>
                    <button onClick={() => { setModuleActif("tassements"); setOngletActif(0); }}
                      style={{ width:"100%", background:"#dc2626", border:"none", color:"#fff", borderRadius:6, padding:"7px 12px", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                      {t("copilote_tab.lien_pieux_tass_btn")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          </>)}
          {/* ── FIN KB STATIQUE ── */}

        </div>
      </div>
    </div>
  );
}

// =============================================================================
// APP PRINCIPALE — REFONTE COMPLÈTE
// =============================================================================

// =============================================================================
// CAS TYPIQUES ALGÉRIE — Base de connaissance enrichie
// Sert à la fois de référence pour le moteur et d'aide contextuelle dans l'UI
// =============================================================================

export default TabCoPilote;
